/**
 * Audit API Service
 *
 * Functions for interacting with the audit log REST endpoints.
 * The Python API proxies these to the Go audit service.
 */

import api from "./api";
import type {
  AuditEntry,
  AuditEventList,
  AuditFilters,
  AuditStats,
  AuditVerification,
} from "../types/audit";

// ─── Snake→Camel transforms ─────────────────────────────────

function transformEntry(raw: Record<string, unknown>): AuditEntry {
  return {
    id: raw.id as string,
    eventType: raw.event_type as string,
    eventData: (typeof raw.event_data === "string"
      ? JSON.parse(raw.event_data as string)
      : raw.event_data) as Record<string, unknown>,
    sourceService: raw.source_service as string,
    sourceTopic: raw.source_topic as string,
    prevHash: raw.prev_hash as string,
    hash: raw.hash as string,
    createdAt: raw.created_at as string,
  };
}

function transformEventList(raw: Record<string, unknown>): AuditEventList {
  return {
    entries: ((raw.entries as Record<string, unknown>[]) ?? []).map(
      transformEntry,
    ),
    total: raw.total as number,
    page: raw.page as number,
    pageSize: raw.page_size as number,
  };
}

function transformVerification(
  raw: Record<string, unknown>,
): AuditVerification {
  return {
    isValid: raw.is_valid as boolean,
    entriesChecked: raw.entries_checked as number,
    firstBrokenId: (raw.first_broken_id as string) || null,
    verifiedAt: raw.verified_at as string,
  };
}

function transformStats(raw: Record<string, unknown>): AuditStats {
  return {
    totalEvents: raw.total_events as number,
    eventsByType: raw.events_by_type as Record<string, number>,
    eventsBySource: raw.events_by_source as Record<string, number>,
    oldestEvent: (raw.oldest_event as string) || null,
    newestEvent: (raw.newest_event as string) || null,
    chainIntact: raw.chain_intact as boolean,
  };
}

// ─── API Functions ──────────────────────────────────────────

export async function getAuditEvents(
  filters: AuditFilters,
): Promise<AuditEventList> {
  const response = await api.get<Record<string, unknown>>("/audit/events", {
    params: {
      page: filters.page,
      page_size: filters.pageSize,
      ...(filters.eventType && { event_type: filters.eventType }),
      ...(filters.sourceService && { source_service: filters.sourceService }),
    },
  });
  return transformEventList(response.data);
}

export async function getAuditEvent(id: string): Promise<AuditEntry> {
  const response = await api.get<Record<string, unknown>>(
    `/audit/events/${id}`,
  );
  return transformEntry(response.data);
}

export async function verifyAuditChain(): Promise<AuditVerification> {
  const response = await api.post<Record<string, unknown>>("/audit/verify");
  return transformVerification(response.data);
}

export async function getAuditStats(): Promise<AuditStats> {
  const response = await api.get<Record<string, unknown>>("/audit/stats");
  return transformStats(response.data);
}
